/**
 * The player shell: anonymous play across all five games, score banking via
 * magic-link accounts, consent banner (§8.1), leaderboards (§7.3), Garden.
 * (The prize draw was removed 2026-08-30 pending MGA-compliance clarity.)
 */

import type { GameModule } from '@hnplay/game-sdk';
import { encodeRunLog } from '@hnplay/game-sdk';
import { HEALTH_GOALS, MALTA_LOCALITIES } from '@hnplay/shared';
import { api, type ShellConfig } from './shell/api.js';
import { showConsentBanner } from './shell/consent-banner.js';
import type { ProductInfo } from './shell/choice-overlay.js';
import { playGame, type PlayResult } from './shell/game-host.js';
import './style.css';

const GAME_LOADERS: Record<string, () => Promise<GameModule<unknown>>> = {
  chain: () => import('@hnplay/game-chain').then((m) => m.chainModule as GameModule<unknown>),
  stack: () => import('@hnplay/game-stack').then((m) => m.stackModule as GameModule<unknown>),
  bloodstream: () => import('@hnplay/game-bloodstream').then((m) => m.bloodstreamModule as GameModule<unknown>),
  tilt: () => import('@hnplay/game-tilt').then((m) => m.tiltModule as GameModule<unknown>),
  dash: () => import('@hnplay/game-dash').then((m) => m.dashModule as GameModule<unknown>),
};
const GAME_TITLES: Record<string, string> = {
  chain: 'Chain Reaction',
  stack: 'Stack',
  bloodstream: 'Bloodstream',
  tilt: 'Tilt',
  dash: 'Delivery Dash',
};

/** One source for the pre-run how-to cards AND the #/guide page. */
const GAME_GUIDE: Record<string, { emoji: string; blurb: string; how: string[] }> = {
  chain: {
    emoji: '🧪',
    blurb: 'Link matching compounds into chains.',
    how: [
      'Hold and sweep across matching compounds to link a chain — release to score it.',
      'Longer chains and quick follow-ups multiply your score.',
      'Compounds that decay unused cost points and reset your combo.',
      'Every 3rd chain opens a catalyst choice — pick a product for a bigger multiplier.',
    ],
  },
  stack: {
    emoji: '📦',
    blurb: 'Build the tallest product tower.',
    how: [
      'Tap anywhere (or press Space) to drop the swinging box onto the tower.',
      'Only the overlap survives — the overhang is sliced off.',
      'A perfect drop restores the full width and grows your precision multiplier.',
      'Miss the tower completely and the run ends.',
    ],
  },
  bloodstream: {
    emoji: '🩸',
    blurb: 'Catch only the orbs that match YOUR goal.',
    how: [
      'Pick your health goal first — its name and colour stay pinned at the top of the screen.',
      'Slide left / right (or use arrow keys) to circle the vessel wall.',
      'Catch only orbs in your goal’s colour — each one builds your combo.',
      'Other colours break the combo, and the dark clots end the run — dodge those.',
    ],
  },
  tilt: {
    emoji: '🌀',
    blurb: 'Roll the droplet through the maze.',
    how: [
      'Tilt your phone (or drag / use arrow keys) to roll the droplet.',
      'Pass through every glowing gate before the clock runs out.',
      'Walls are safe — rushing is the real enemy. Remaining time is bonus points.',
    ],
  },
  dash: {
    emoji: '🛵',
    blurb: 'Deliver parcels across a little Malta.',
    how: [
      'Hold left / right (screen halves or arrow keys) to steer — you accelerate automatically.',
      'Ride to the orange light beam: that’s your delivery. The Malta minimap shows the way.',
      'Each delivery adds +8s to the clock; hitting a bus costs −3s.',
      'Every 3rd delivery is a depot stop — pick a parcel for bonus time.',
    ],
  },
  garden: {
    emoji: '🌿',
    blurb: 'Your garden grows between visits.',
    how: [
      'Every verified run earns a seed.',
      'Plant Mediterranean botanicals — aloe, rosemary, prickly pear — and they grow in real time.',
      'Sign in to keep your garden (and your scores) forever.',
    ],
  },
};

/** Quick-start card shown before each run — one tap to start. */
function showHowTo(game: string): Promise<void> {
  const guide = GAME_GUIDE[game];
  if (!guide) return Promise.resolve();
  return new Promise((resolve) => {
    const el = h(`
      <div class="hnp-modal">
        <div class="hnp-modal__card">
          <h2>${guide.emoji} ${GAME_TITLES[game] ?? game}</h2>
          <ul class="hnp-howto">
            ${guide.how.map((line) => `<li>${line}</li>`).join('')}
          </ul>
          <button type="button" class="cta cta--big" id="howto-go">▶ Play</button>
        </div>
      </div>`);
    document.body.appendChild(el);
    el.querySelector('#howto-go')!.addEventListener('click', () => {
      el.remove();
      resolve();
    });
  });
}

const app = document.querySelector<HTMLElement>('#app');
if (!app) throw new Error('missing #app');

let config: ShellConfig | null = null;

function h(html: string): HTMLElement {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild as HTMLElement;
}

function header(): HTMLElement {
  const signedIn = config?.player.verified === true;
  return h(`
    <header class="hnp-header">
      <a class="hnp-header__brand" href="#/">HN <em>Play</em></a>
      <nav>
        <a href="#/garden">🌿 Garden</a>
        <a href="#/leaderboard">Leaderboard</a>
        <a href="${signedIn ? '#/profile' : '#/'}" id="nav-signin" class="hnp-header__draw">${signedIn ? 'My profile' : 'Sign in'}</a>
      </nav>
    </header>`);
}

/**
 * Every screen except home gets the same back affordance. Home is the root, so
 * a back link there would be a no-op; the score screen passes its own (it is
 * rendered in place, without a hash change).
 */
function backBar(href = '#/', label = 'Back'): HTMLElement {
  return h(`<div class="hnp-backbar"><a class="hnp-back" href="${href}">‹ ${label}</a></div>`);
}

function screen(...children: HTMLElement[]): void {
  const hash = location.hash || '#/';
  const atHome = hash === '#/' || hash === '#';
  app!.replaceChildren(header(), ...(atHome ? [] : [backBar()]), ...children);
  document.querySelector('#nav-signin')?.addEventListener('click', (e) => {
    if (config?.player.verified !== true) {
      e.preventDefault();
      registerModal();
    }
  });
  window.scrollTo(0, 0);
}

// ── Registration modal — magic-link account (saves scores + garden) ─────────
function registerModal(pendingRunId?: string): void {
  const copy = {
    title: pendingRunId ? 'Bank this score' : 'Sign in / create account',
    text: pendingRunId
      ? 'Create your free account to save this score to the leaderboard under your name — and keep your garden growing across devices.'
      : 'One email, no password: we send you a sign-in link. Your scores, display name and garden live on your account.',
  };
  const el = h(`
    <div class="hnp-modal">
      <div class="hnp-modal__card">
        <button class="hnp-modal__close" aria-label="Close">×</button>
        <h2>${copy.title}</h2>
        <p>${copy.text}</p>
        <form class="hnp-modal__form">
          <input type="email" name="email" placeholder="you@example.com" required maxlength="190">
          <label class="hnp-modal__terms"><input type="checkbox" required> I accept the <a href="#/terms" target="_blank">terms</a> and the <a href="#/privacy" target="_blank">privacy notice</a></label>
          <label class="hnp-modal__terms"><input type="checkbox" name="mkt"> Email me offers and news from Health Needs (optional)</label>
          <button type="submit" class="cta">Email me a sign-in link</button>
        </form>
        <p class="hnp-modal__note" hidden></p>
      </div>
    </div>`);
  document.body.appendChild(el);
  el.querySelector('.hnp-modal__close')!.addEventListener('click', () => el.remove());
  el.addEventListener('click', (e) => {
    if (e.target === el) el.remove();
  });
  el.querySelector('form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const email = (new FormData(form).get('email') as string) ?? '';
    const note = el.querySelector('.hnp-modal__note') as HTMLElement;
    void api.magicLink(email, pendingRunId).then((res) => {
      note.hidden = false;
      note.textContent = res.ok
        ? 'Check your inbox — the link signs you in and works once, for 30 minutes.'
        : res.status === 429
          ? 'Too many requests — try again in a few minutes.'
          : 'Something went wrong — try again.';
      if (res.ok) form.querySelectorAll('input,button').forEach((n) => ((n as HTMLInputElement).disabled = true));
    });
  });
}

// ── Screens ─────────────────────────────────────────────────────────────────
function renderHome(): void {
  const game = config?.game ?? 'chain';
  const others = (config?.enabledGames ?? []).filter((g) => g !== game && g in GAME_LOADERS);
  screen(
    h(`
    <main class="hnp-home">
      <section class="hnp-hero">
        <h1>${GAME_TITLES[game] ?? game}</h1>
        <p>A short game from Health Needs. Play as often as you like — no account needed.</p>
        <button class="cta cta--big" id="play-now">▶ Play now</button>
        <p class="note">One hand, portrait — 30 seconds to learn. <a href="#/guide">How to play</a></p>
      </section>
      ${
        others.length
          ? `<section class="hnp-panel">
              <h2>More games</h2>
              <div class="hnp-gamesrow">
                ${others.map((g) => `<button type="button" data-play="${g}">${GAME_TITLES[g] ?? g}</button>`).join('')}
              </div>
            </section>`
          : ''
      }
      <section class="hnp-panel">
        <h2>Leaderboard</h2>
        <p>Best verified runs, per game. Bragging rights, badges and garden keepsakes only — nothing here is ever worth money.</p>
        <a class="cta cta--ghost" href="#/leaderboard">See the board</a>
        <a class="cta cta--ghost" href="#/guide" style="margin-left:8px">How to play</a>
      </section>
    </main>`),
  );
  document.querySelector('#play-now')!.addEventListener('click', () => void startPlay());
  document.querySelectorAll<HTMLButtonElement>('[data-play]').forEach((b) =>
    b.addEventListener('click', () => void startPlay(b.dataset['play'] as string)),
  );
}

/** §6.3: the declared Health Goal is chosen BEFORE the run and pinned server-side. */
function pickGoal(): Promise<number> {
  return new Promise((resolve) => {
    const el = h(`
      <div class="hnp-modal">
        <div class="hnp-modal__card">
          <h2>What's your health goal?</h2>
          <p>Your bloodstream carries what you chase — catch the matching orbs.</p>
          <div class="hnp-goalgrid">
            ${HEALTH_GOALS.map((g, i) => `<button type="button" data-goal="${i}">${g}</button>`).join('')}
          </div>
        </div>
      </div>`);
    document.body.appendChild(el);
    el.querySelectorAll<HTMLButtonElement>('[data-goal]').forEach((b) =>
      b.addEventListener('click', () => {
        el.remove();
        resolve(Number(b.dataset['goal']));
      }),
    );
  });
}

/** §6.5: home locality — the same list as the WooCommerce reports. */
function pickLocality(): Promise<string> {
  return new Promise((resolve) => {
    const el = h(`
      <div class="hnp-modal">
        <div class="hnp-modal__card">
          <h2>Where do we deliver to you?</h2>
          <p>Pick your home locality — then ride!</p>
          <select class="hnp-localityselect">
            ${MALTA_LOCALITIES.map((l) => `<option>${l}</option>`).join('')}
          </select>
          <button type="button" class="cta" id="loc-go">Start delivering</button>
        </div>
      </div>`);
    document.body.appendChild(el);
    el.querySelector('#loc-go')!.addEventListener('click', () => {
      const v = (el.querySelector('select') as HTMLSelectElement).value;
      el.remove();
      resolve(v);
    });
  });
}

async function startPlay(gameOverride?: string): Promise<void> {
  const game = gameOverride ?? config?.game ?? 'chain';
  const loader = GAME_LOADERS[game] ?? GAME_LOADERS['chain']!;

  let gameParams: Record<string, number | string> | undefined;
  const isE2E = new URLSearchParams(location.search).has('e2e-instant');
  if (!isE2E) await showHowTo(game);
  if (game === 'bloodstream' && !isE2E) gameParams = { goal: await pickGoal() };
  if (game === 'dash' && !isE2E) gameParams = { locality: await pickLocality() };

  app!.replaceChildren(h(`<div class="hnp-stage" id="stage"><p class="hnp-loading">warming up…</p></div>`));
  const stage = document.querySelector<HTMLElement>('#stage')!;

  const issued = await api.issueRun(game in GAME_LOADERS ? game : 'chain', gameParams);
  let gameModule: GameModule<unknown>;
  try {
    gameModule = await loader();
    sessionStorage.removeItem('hnp-reload-once');
  } catch {
    // A deploy replaced the hashed chunks under a cached index.html — one
    // forced reload fetches the fresh entrypoint; a second failure is real.
    if (!sessionStorage.getItem('hnp-reload-once')) {
      sessionStorage.setItem('hnp-reload-once', '1');
      location.reload();
      return;
    }
    stage.replaceChildren(
      h(`<p class="hnp-loading">This game couldn’t load — check your connection and refresh.</p>`),
    );
    return;
  }
  stage.querySelector('.hnp-loading')?.remove();

  const instant = new URLSearchParams(location.search).has('e2e-instant');
  const result = await playGame({
    module: gameModule,
    seed: issued.seed,
    config: issued.config,
    products: new Map(issued.products.map((p: ProductInfo) => [p.id, p])),
    stage,
    instant,
    // §6.4: tilt permission is a user gesture — the pre-run gate provides it,
    // with the on-screen fallback built in from day one.
    gate:
      game === 'tilt' && !instant
        ? async (input, overlay) => {
            await new Promise<void>((resolve) => {
              const el = h(`
                <div class="hnp-choice">
                  <div class="hnp-choice__panel">
                    <p class="hnp-choice__title">Tilt to play</p>
                    <p style="font-size:.85rem;color:var(--muted)">Roll the droplet by tilting your phone — or play with drag / arrow keys.</p>
                    <button type="button" class="cta" id="tilt-on">Enable tilt &amp; play</button>
                    <button type="button" class="cta cta--ghost" id="tilt-off" style="margin-top:8px">Play without tilt</button>
                  </div>
                </div>`);
              overlay.appendChild(el);
              el.querySelector('#tilt-on')!.addEventListener('click', () => {
                void input.enableOrientation().finally(() => {
                  el.remove();
                  resolve();
                });
              });
              el.querySelector('#tilt-off')!.addEventListener('click', () => {
                el.remove();
                resolve();
              });
            });
          }
        : undefined,
  });

  if (result.aborted) {
    // Quit mid-run: nothing is submitted, nothing is scored.
    location.hash = '#/';
    renderHome();
    return;
  }

  const encoded = await encodeRunLog(result.frames);
  let runId: string | null = null;
  try {
    const submitted = await api.submitRun(issued.sessionToken, encoded, result.score);
    runId = submitted.runId;
  } catch {
    /* offline / rejected — score screen still shows, banking unavailable */
  }
  renderScore(result, runId, game, new Map(issued.products.map((p: ProductInfo) => [p.id, p])));
}

/** §9.6 (honest version): per-product core add-to-cart links + a store link. */
function stackEndHtml(result: PlayResult, products: Map<string, ProductInfo>): string {
  const floors = Number(result.telemetry.gameSpecific['floors'] ?? 0);
  const chosenIds = [
    ...new Set(
      result.telemetry.choices
        .filter((c) => c.chosenIndex >= 0)
        .map((c) => c.offeredIds[c.chosenIndex] as string),
    ),
  ];
  const rows = chosenIds
    .map((id) => {
      const p = products.get(id);
      if (!p) return '';
      const name = p.name.replace(/</g, '&lt;');
      const add = p.addToCartUrl
        ? `<a class="hnp-stackend__add" data-store-click href="${p.addToCartUrl}" target="_blank" rel="noopener">Add to basket</a>`
        : '<span class="note">available in-store soon</span>';
      return `<li>${p.imageUrl ? `<img src="${p.imageUrl}" alt="">` : '<span class="hnp-choice__swatch"></span>'}<span>${name}</span>${add}</li>`;
    })
    .join('');
  const storeBase = [...products.values()].map((p) => p.storeUrl ?? p.addToCartUrl).find(Boolean);
  const shopAll = storeBase
    ? `<a class="cta cta--ghost" data-store-click href="${new URL('/', storeBase as string).toString()}" target="_blank" rel="noopener">Shop your stack at Health Needs</a>`
    : '';
  return `
    <section class="hnp-stackend">
      <h2>Your stack reached ${floors} floor${floors === 1 ? '' : 's'} 🏗️</h2>
      ${chosenIds.length ? `<p>Built with:</p><ul class="hnp-stackend__list">${rows}</ul>` : ''}
      ${shopAll}
    </section>`;
}

function renderScore(result: PlayResult, runId: string | null, gameId = 'chain', products: Map<string, ProductInfo> = new Map()): void {
  const signedIn = config?.player.verified === true;
  screen(
    h(`
    <main class="hnp-score">
      <p class="end-kicker">Run complete</p>
      <h1 class="end-score">${result.score.toLocaleString()}</h1>
      <p class="hnp-score__rank" id="rank-line">Verifying your run…</p>
      ${
        signedIn
          ? `<p>Saved to your account.</p>`
          : `<button class="cta" id="bank">Create an account to bank this score</button>`
      }
      ${gameId === 'stack' ? stackEndHtml(result, products) : ''}
      <div class="hnp-score__row">
        <button class="cta cta--ghost" id="again">Play again</button>
        <a class="cta cta--ghost" href="#/leaderboard">Leaderboard</a>
        <a class="cta cta--ghost" href="#/" id="score-home">Home</a>
      </div>
    </main>`),
  );
  // §9.4: first-party record of every outbound store click.
  document.querySelectorAll('[data-store-click]').forEach((a) =>
    a.addEventListener('click', () => void api.storeClick(gameId)),
  );
  document.querySelector('#again')!.addEventListener('click', () => void startPlay(gameId));
  document.querySelector('#bank')?.addEventListener('click', () => registerModal(runId ?? undefined));

  const rankLine = document.querySelector<HTMLElement>('#rank-line')!;
  if (!runId) {
    rankLine.textContent = 'Run not submitted — check your connection and play again.';
    return;
  }
  let polls = 0;
  const poll = async (): Promise<void> => {
    polls += 1;
    try {
      const status = await api.runStatus(runId);
      if (status.status === 'VERIFIED') {
        rankLine.innerHTML = status.rank ? `That’s <b>#${status.rank}</b> on the all-time board.` : 'Verified.';
        return;
      }
      if (status.status === 'INVALID') {
        rankLine.textContent = 'This run could not be verified.';
        return;
      }
    } catch {
      /* keep polling */
    }
    if (polls < 15) setTimeout(() => void poll(), 1500);
    else rankLine.textContent = 'Verification is taking a while — your score will appear on the board once done.';
  };
  void poll();
}

async function renderLeaderboard(gameId?: string): Promise<void> {
  const game = gameId ?? config?.game ?? 'chain';
  const board = await api.leaderboard(game);
  const games = (config?.enabledGames ?? [game]).filter((g) => g in GAME_LOADERS);
  screen(
    h(`
    <main class="hnp-board">
      <h1>${GAME_TITLES[game] ?? game} — all-time</h1>
      <div class="hnp-board__tabs">
        ${games.map((g) => `<button class="${g === game ? 'on' : ''}" data-game="${g}">${GAME_TITLES[g] ?? g}</button>`).join('')}
      </div>
      ${board.me ? `<p class="hnp-board__me">Your best: <b>${board.me.score.toLocaleString()}</b> — rank <b>#${board.me.rank}</b></p>` : ''}
      <table class="hnp-board__table">
        <tbody>
          ${board.rows
            .map(
              (r) =>
                `<tr class="${r.isMe ? 'me' : ''}"><td>#${r.rank}</td><td>${r.handle.replace(/</g, '&lt;')}</td><td>${r.score.toLocaleString()}</td></tr>`,
            )
            .join('')}
        </tbody>
      </table>
      ${board.rows.length === 0 ? '<p class="note">No verified runs yet — be the first.</p>' : ''}
      <p class="note">Leaderboard places win badges and bragging rights only — never anything of monetary value.</p>
    </main>`),
  );
  document.querySelectorAll<HTMLButtonElement>('.hnp-board__tabs button').forEach((b) =>
    b.addEventListener('click', () => void renderLeaderboard(b.dataset['game'] as string)),
  );
}

function renderProfile(): void {
  if (!config?.player.verified) {
    location.hash = '#/';
    return;
  }
  const p = config.player;
  screen(
    h(`
    <main class="hnp-profile">
      <h1>My profile</h1>
      <form class="hnp-profile__form">
        <label>Display name <input name="displayName" maxlength="24" value="${(p.displayName ?? '').replace(/"/g, '&quot;')}" placeholder="Shown on the leaderboard"></label>
        <label>My health goal
          <select name="declaredGoal">
            <option value="">Choose…</option>
            ${HEALTH_GOALS.map((g) => `<option ${p.declaredGoal === g ? 'selected' : ''}>${g}</option>`).join('')}
          </select>
        </label>
        <label class="hnp-profile__check"><input type="checkbox" name="leaderboardConsent" ${p.leaderboardConsent ? 'checked' : ''}> Show my display name publicly on leaderboards</label>
        <button type="submit" class="cta">Save profile</button>
        <p class="hnp-profile__msg" hidden></p>
      </form>
      <button class="cta cta--ghost" id="logout">Sign out</button>
    </main>`),
  );
  document.querySelector('form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const msg = form.querySelector('.hnp-profile__msg') as HTMLElement;
    const displayName = (fd.get('displayName') as string) || '';
    const declaredGoal = (fd.get('declaredGoal') as string) || '';
    void api
      .profile({
        ...(displayName ? { displayName } : {}),
        ...(declaredGoal ? { declaredGoal } : {}),
        leaderboardConsent: fd.get('leaderboardConsent') === 'on',
      })
      .then(async (res) => {
        msg.hidden = false;
        if (res.ok) {
          msg.textContent = 'Saved.';
          config = await api.config();
        } else {
          msg.textContent = 'Could not save — check the fields.';
        }
      });
  });
  document.querySelector('#logout')!.addEventListener('click', () => {
    void api.logout().then(async () => {
      config = await api.config();
      location.hash = '#/';
    });
  });
}

// ── The Garden (§6.6): the reason to come back tomorrow ─────────────────────
let gardenCleanup: (() => void) | null = null;

async function renderGarden(): Promise<void> {
  gardenCleanup?.();
  const data = await api.garden();
  screen(
    h(`
    <main class="hnp-gardenpage">
      <h1>Your garden</h1>
      <p>Every run you finish earns a seed. Plant botanicals and they keep growing while you're away —
         come back tomorrow and see. <span class="note">Garden items are keepsakes only: they can never be
         redeemed, traded or sold.</span></p>
      <p class="hnp-gardenpage__seeds">🌰 <b id="seed-count">${data.seeds}</b> seed${data.seeds === 1 ? '' : 's'} — tap an empty plot to plant</p>
      <div class="hnp-gardenstage" id="garden-stage"></div>
    </main>`),
  );
  const stage = document.querySelector<HTMLElement>('#garden-stage')!;
  const { acquireSharedStage } = await import('./shell/game-host.js');
  const { mountGardenScene } = await import('@hnplay/game-garden');
  const { canvas, renderer } = acquireSharedStage();
  stage.appendChild(canvas);

  let current = data;
  const sceneHandle = mountGardenScene(renderer, stage, (slot) => {
    if (current.slots[slot]) return; // occupied
    if (current.seeds < 1) {
      alert('No seeds yet — finish a run to earn one!');
      return;
    }
    const el = h(`
      <div class="hnp-modal">
        <div class="hnp-modal__card">
          <h2>Plant this plot</h2>
          <div class="hnp-goalgrid">
            ${['ashwagandha', 'turmeric', 'echinacea', 'elderberry', 'chamomile', 'valerian', 'ginger']
              .map((b) => `<button type="button" data-b="${b}">${b}</button>`)
              .join('')}
          </div>
        </div>
      </div>`);
    document.body.appendChild(el);
    el.addEventListener('click', (e) => {
      if (e.target === el) el.remove();
    });
    el.querySelectorAll<HTMLButtonElement>('[data-b]').forEach((btn) =>
      btn.addEventListener('click', () => {
        el.remove();
        void api.plant(btn.dataset['b'] as string, slot).then(async (res) => {
          if (res.ok) {
            current = (await res.json()) as typeof current;
            sceneHandle.update(current.slots as never);
            document.querySelector('#seed-count')!.textContent = String(current.seeds);
          }
        });
      }),
    );
  });
  sceneHandle.update(current.slots as never);

  let raf = 0;
  const applySize = (): void => {
    const w = stage.clientWidth;
    const hgt = stage.clientHeight;
    if (w && hgt) {
      renderer.setSize(w, hgt, false);
      sceneHandle.resize(w, hgt);
    }
  };
  applySize();
  const ro = new ResizeObserver(applySize);
  ro.observe(stage);
  const tick = (): void => {
    raf = requestAnimationFrame(tick);
    sceneHandle.render();
  };
  tick();

  gardenCleanup = (): void => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    sceneHandle.destroy();
    canvas.remove();
    gardenCleanup = null;
  };
}

// ── Guide (#/guide): what HN Play is + how to play every game ───────────────
function renderGuide(): void {
  const playable = (config?.enabledGames ?? Object.keys(GAME_TITLES)).filter((g) => g in GAME_LOADERS);
  const sections = [...playable, 'garden']
    .map((g) => {
      const guide = GAME_GUIDE[g];
      if (!guide) return '';
      const title = g === 'garden' ? 'The Garden' : (GAME_TITLES[g] ?? g);
      const play = g === 'garden' ? '<a class="cta cta--ghost" href="#/garden">Visit the garden</a>' : `<button type="button" class="cta cta--ghost" data-play="${g}">Play it</button>`;
      return `
        <section class="hnp-panel">
          <h2>${guide.emoji} ${title}</h2>
          <p>${guide.blurb}</p>
          <ul class="hnp-howto">${guide.how.map((line) => `<li>${line}</li>`).join('')}</ul>
          ${play}
        </section>`;
    })
    .join('');
  screen(
    h(`
    <main class="hnp-home">
      <section class="hnp-hero hnp-hero--compact">
        <h1>How to play</h1>
        <p>Five short games from Health Needs — free, no account needed. Playing only ever wins badges, titles and garden keepsakes; nothing here is worth money.</p>
      </section>
      ${sections}
      <p class="note" style="text-align:center">Questions about your data? See the <a href="#/privacy">privacy notice</a> · <a href="#/terms">terms</a></p>
    </main>`),
  );
  document.querySelectorAll<HTMLButtonElement>('[data-play]').forEach((b) =>
    b.addEventListener('click', () => void startPlay(b.dataset['play'] as string)),
  );
}

// ── Terms & privacy (drafts — final copy pending review, §15.12) ────────────
function renderStatic(kind: 'terms' | 'privacy'): void {
  const content =
    kind === 'terms'
      ? `<h1>Terms</h1>
         <p class="note">DRAFT — final wording pending review.</p>
         <ul class="hnp-draw__rules">
           <li>The games are free to play. One account per person, 18+.</li>
           <li>Leaderboards and the Garden award recognition only — badges, titles and cosmetic keepsakes. Nothing is redeemable, tradeable or of monetary value.</li>
           <li>Automated play and multiple accounts are prohibited; every run is verified and any flag is reviewed by a person.</li>
         </ul>`
      : `<h1>Privacy</h1>
         <p class="note">DRAFT — final wording pending review. Controller: JV Pharma Ltd (Health Needs), Malta.</p>
         <ul class="hnp-draw__rules">
           <li>We store your sessions, scores and run logs to run the games, and your email (if you register) for your account.</li>
           <li>Abuse prevention uses hashed device/IP signals — never reused for analytics.</li>
           <li>Nothing goes to Google, Meta or Mailchimp unless you opt in via the consent banner; both toggles start OFF.</li>
           <li>Retention: sign-in links 30 days, unplayed seeds 7 days, anonymous play data 90 days after your last visit.</li>
           <li>You can access, correct or erase your data — erasure clears your account, scores, garden and our Mailchimp contact. Complaints: idpc.org.mt.</li>
         </ul>`;
  screen(h(`<main class="hnp-draw">${content}</main>`));
}

// ── Router ──────────────────────────────────────────────────────────────────
function route(): void {
  gardenCleanup?.();
  const hash = location.hash || '#/';
  if (hash.startsWith('#/leaderboard')) void renderLeaderboard();
  else if (hash.startsWith('#/garden')) void renderGarden();
  else if (hash.startsWith('#/profile')) renderProfile();
  else if (hash.startsWith('#/guide')) renderGuide();
  else if (hash.startsWith('#/terms')) renderStatic('terms');
  else if (hash.startsWith('#/privacy')) renderStatic('privacy');
  else renderHome();
}

async function boot(): Promise<void> {
  try {
    config = await api.config();
  } catch {
    app!.replaceChildren(
      h(`<div class="card"><h1>Health Needs Play</h1><p>We can’t reach the game service right now — try again shortly.</p></div>`),
    );
    return;
  }
  if (new URLSearchParams(location.search).get('signedin') === '1') {
    history.replaceState(null, '', location.pathname + location.hash);
  }
  route();
  window.addEventListener('hashchange', route);
  if (!config.consent.decided) {
    showConsentBanner(() => {
      void api.config().then((c) => {
        config = c;
      });
    });
  }
}

void boot();
